"use client";

/**
 * The application's router, in this repository rather than a dependency.
 *
 * The surface is small — five paths, a handful of navigations — and the app was written
 * against two imports (`next/navigation`, `next/link`) whose shapes this reproduces, so
 * the move off Next is an import swap at the call sites rather than a rewrite of them.
 * A router dependency was tried first and rejected on a React peer range this workspace
 * cannot satisfy without moving React, which the harness build pins.
 *
 * `refresh()` is not a re-render: it bumps a revision that data-loading effects depend on,
 * which is what the Next primitive meant at these call sites — re-read the server state.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type AnchorHTMLAttributes, type MouseEvent, type ReactNode,
} from "react";

interface Location { readonly pathname: string; readonly search: string }

interface RouterValue {
  readonly location: Location;
  readonly revision: number;
  readonly navigate: (to: string, options?: { replace?: boolean }) => void;
  readonly refresh: () => void;
}

const RouterContext = createContext<RouterValue | undefined>(undefined);

function currentLocation(): Location {
  return { pathname: window.location.pathname, search: window.location.search };
}

export function RouterProvider({ children }: { children: ReactNode }): ReactNode {
  const [location, setLocation] = useState<Location>(currentLocation);
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    const onPopState = (): void => { setLocation(currentLocation()); };
    window.addEventListener("popstate", onPopState);
    return () => { window.removeEventListener("popstate", onPopState); };
  }, []);

  const navigate = useCallback((to: string, options?: { replace?: boolean }): void => {
    const url = new URL(to, window.location.origin);
    if (options?.replace) window.history.replaceState(null, "", url);
    else window.history.pushState(null, "", url);
    setLocation(currentLocation());
    window.scrollTo({ top: 0 });
  }, []);

  const refresh = useCallback((): void => { setRevision(value => value + 1); }, []);

  const value = useMemo<RouterValue>(() => ({ location, revision, navigate, refresh }), [location, revision, navigate, refresh]);
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

function useRouterContext(): RouterValue {
  const value = useContext(RouterContext);
  if (value === undefined) throw new Error("the router is not mounted: wrap the application in <RouterProvider>");
  return value;
}

/** The Next route helpers, reproduced. */
export function useRouter(): { push: (to: string) => void; replace: (to: string) => void; refresh: () => void; back: () => void; forward: () => void } {
  const { navigate, refresh } = useRouterContext();
  return useMemo(() => ({
    push: (to: string) => { navigate(to); },
    replace: (to: string) => { navigate(to, { replace: true }); },
    refresh,
    back: () => { window.history.back(); },
    forward: () => { window.history.forward(); },
  }), [navigate, refresh]);
}

export function usePathname(): string {
  return useRouterContext().location.pathname;
}

export function useSearchParams(): URLSearchParams {
  const { search } = useRouterContext().location;
  return useMemo(() => new URLSearchParams(search), [search]);
}

/** The revision a data-loading effect depends on, so `refresh()` re-reads server state. */
export function useRefreshRevision(): number {
  return useRouterContext().revision;
}

/** One navigation handler, shared by `Link` and imperative callers. */
export function navigate(to: string, options?: { replace?: boolean }): void {
  window.history[options?.replace ? "replaceState" : "pushState"](null, "", new URL(to, window.location.origin));
  window.dispatchEvent(new PopStateEvent("popstate"));
  window.scrollTo({ top: 0 });
}

export interface LinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  readonly href: string;
  readonly children: ReactNode;
}

export function Link({ href, children, onClick, ...rest }: LinkProps): ReactNode {
  const { navigate: go } = useRouterContext();
  const handle = (event: MouseEvent<HTMLAnchorElement>): void => {
    onClick?.(event);
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    go(href);
  };
  return <a href={href} onClick={handle} {...rest}>{children}</a>;
}

export default Link;
